// src/pages/HomeView/components/EfficiencyReport/EfficiencyReport.tsx
import React, { useState, useCallback } from 'react';
import { RotateCcw, ChevronUp, ChevronDown, ChevronsUpDown, Plus } from 'lucide-react';
import { authStorage } from '@lib/storage';
import ReportService from '@services/ReportService';
import { useInfoWindow } from '@contexts/InfoWindowContext';

// ─── Types ────────────────────────────────────────────────────────────────────

interface GeneralRow {
  id: number;
  month: string;
  workingcapacity: number;
  loggedhours: string;
  ptodays: number;
  productivity: string;
  efficiency: string;
  showbutton: boolean;
}

interface DetailRow {
  id: number;
  month: string;
  taskname: string;
  loggedhours: string;
  estimated: string;
  remaining: string;
  actual: string;
  burnedhours: string;
  conversationid: string;
}

type SortColumn =
  | 'default'
  | 'month'
  | 'workingcapacity'
  | 'loggedhours'
  | 'ptodays'
  | 'productivity'
  | 'efficiency';
type SortDir = '' | 'asc' | 'desc';

// ─── Helpers ─────────────────────────────────────────────────────────────────

function buildYearOptions()
{
  const y = new Date().getFullYear();
  return [y - 2, y - 1, y];
}

function fmt(n: number | string)
{
  return parseFloat(String(n)).toFixed(2);
}

function getDueDate(startOfYear: Date): Date
{
  const today = new Date();
  return today.getFullYear() === startOfYear.getFullYear()
    ? today
    : new Date(startOfYear.getFullYear(), 11, 31);
}

function toUSDate(d: Date): string
{
  return d.toLocaleDateString('en-US', { month: '2-digit', day: '2-digit', year: 'numeric' });
}

// ─── Sort icon ────────────────────────────────────────────────────────────────

function SortIcon({ col, sortedBy, dir }: { col: string; sortedBy: string; dir: SortDir })
{
  if (sortedBy !== col) return <ChevronsUpDown className="inline w-3 h-3 ml-1 text-gray-400" />;
  return dir === 'asc' ? (
    <ChevronUp className="inline w-3 h-3 ml-1 text-corgee-blue" />
  ) : (
    <ChevronDown className="inline w-3 h-3 ml-1 text-corgee-blue" />
  );
}

// ─── Component ────────────────────────────────────────────────────────────────

export const EfficiencyReport: React.FC = () =>
{
  const { openInfoWindow } = useInfoWindow();
  const years = buildYearOptions();
  const [selectedYear, setSelectedYear] = useState<number>(new Date().getFullYear());
  const [isLoading, setIsLoading] = useState(false);

  // Summary totals
  const [totalWorkingCapacity, setTotalWorkingCapacity] = useState('');
  const [totalLoggedHours, setTotalLoggedHours] = useState('');
  const [totalPTODays, setTotalPTODays] = useState(0);
  const [totalProductivity, setTotalProductivity] = useState('');
  const [totalEfficiency, setTotalEfficiency] = useState('');

  // Table data
  const [originalRows, setOriginalRows] = useState<GeneralRow[]>([]);
  const [rows, setRows] = useState<GeneralRow[]>([]);
  const [sortedBy, setSortedBy] = useState<SortColumn>('default');
  const [sortDir, setSortDir] = useState<SortDir>('');
  const [resultMessage, setResultMessage] = useState('Please apply filters to display results.');

  // Details
  const [isShowDetails, setIsShowDetails] = useState(false);
  const [detailRows, setDetailRows] = useState<DetailRow[]>([]);
  const [rawResponse, setRawResponse] = useState<any>(null);

  // ── Fetch ──────────────────────────────────────────────────────────────────

  const getResults = useCallback(async () =>
  {
    setIsLoading(true);
    setIsShowDetails(false);
    setDetailRows([]);

    const initialDate = new Date(selectedYear, 0, 1);
    const dueDate = getDueDate(initialDate);

    const payload = {
      StartDate: toUSDate(initialDate),
      DueDate: toUSDate(dueDate),
      PersonId: authStorage.getPersonId() || '',
      WebUrl: '',
    };

    try
    {
      const response = await ReportService.GetEfficiencyData(payload);
      setRawResponse(response);
      fillTable(response);
    }
    catch
    {
      cleanResults();
    }
    finally
    {
      setIsLoading(false);
    }
  }, [selectedYear]);

  // ── Table fill ────────────────────────────────────────────────────────────

  function fillTable(response: any)
  {
    if (!response?.general)
    {
      cleanResults();
      return;
    }

    let tWC = 0,
      tLH = 0,
      tPTO = 0,
      tProd = 0,
      tEff = 0;
    const filled: GeneralRow[] = response.general.map((el: any) =>
    {
      tWC += el.workingCapacity;
      tLH += el.loggedHours;
      tPTO += el.ptoDays;
      tProd += el.productivity;
      tEff += el.efficiency;
      return {
        id: el.monthId,
        month: el.month,
        workingcapacity: el.workingCapacity,
        loggedhours: fmt(el.loggedHours),
        ptodays: el.ptoDays,
        productivity: fmt(el.productivity),
        efficiency: fmt(el.efficiency),
        showbutton: parseFloat(el.loggedHours) > 0,
      };
    });

    const activeMonths = filled.filter(r => r.showbutton).length;
    const avgProd = activeMonths > 0 ? tProd / activeMonths : 0;
    const avgEff = activeMonths > 0 ? tEff / activeMonths : 0;

    setOriginalRows(filled);
    setRows(filled);
    setSortedBy('default');
    setSortDir('');
    setTotalWorkingCapacity(tWC.toFixed(2));
    setTotalLoggedHours(tLH.toFixed(2));
    setTotalPTODays(tPTO);
    setTotalProductivity(avgProd.toFixed(2));
    setTotalEfficiency(avgEff.toFixed(2));
    setResultMessage('');
  }

  function cleanResults()
  {
    setOriginalRows([]);
    setRows([]);
    setTotalWorkingCapacity('');
    setTotalLoggedHours('');
    setTotalPTODays(0);
    setTotalProductivity('');
    setTotalEfficiency('');
    setIsShowDetails(false);
    setResultMessage('No results found');
  }

  // ── Sorting ───────────────────────────────────────────────────────────────

  function sortTableBy(col: SortColumn)
  {
    let dir: SortDir;
    if (col === 'default')
    {
      dir = '';
    }
    else if (sortedBy === col && sortDir === 'asc')
    {
      dir = 'desc';
    }
    else if (sortedBy === col && sortDir === 'desc')
    {
      setSortedBy('default');
      setSortDir('');
      setRows(originalRows);
      return;
    }
    else
    {
      dir = 'asc';
    }

    setSortedBy(col);
    setSortDir(dir);
    setRows(sortByColumn(col, dir));
  }

  function sortByColumn(col: SortColumn, dir: SortDir): GeneralRow[]
  {
    if (col === 'default' || !dir) return originalRows;
    const src = [...originalRows];
    const asc = dir === 'asc';
    switch (col)
    {
      case 'month':
        return src.sort((a, b) =>
          asc ? a.month.localeCompare(b.month) : b.month.localeCompare(a.month),
        );
      case 'workingcapacity':
        return src.sort((a, b) =>
          asc ? a.workingcapacity - b.workingcapacity : b.workingcapacity - a.workingcapacity,
        );
      case 'loggedhours':
        return src.sort((a, b) =>
          asc
            ? parseFloat(a.loggedhours) - parseFloat(b.loggedhours)
            : parseFloat(b.loggedhours) - parseFloat(a.loggedhours),
        );
      case 'ptodays':
        return src.sort((a, b) => (asc ? a.ptodays - b.ptodays : b.ptodays - a.ptodays));
      case 'productivity':
        return src.sort((a, b) =>
          asc
            ? parseFloat(a.productivity) - parseFloat(b.productivity)
            : parseFloat(b.productivity) - parseFloat(a.productivity),
        );
      case 'efficiency':
        return src.sort((a, b) =>
          asc
            ? parseFloat(a.efficiency) - parseFloat(b.efficiency)
            : parseFloat(b.efficiency) - parseFloat(a.efficiency),
        );
      default:
        return originalRows;
    }
  }

  // ── Details ───────────────────────────────────────────────────────────────

  function showDetails(monthId: number)
  {
    if (!rawResponse?.details) return;
    const generalRow = rawResponse.general.find((g: any) => g.monthId === monthId);
    const details: DetailRow[] = rawResponse.details
      .filter((d: any) => d.monthId === monthId)
      .map((d: any, i: number) =>
      {
        const match = d.link?.match(/\/(\d+)$/);
        return {
          id: i,
          month: generalRow?.month ?? '',
          taskname: d.taskName,
          loggedhours: fmt(d.loggedHours),
          estimated: fmt(d.estimated),
          remaining: fmt(d.remaining),
          actual: fmt(d.actual),
          burnedhours: fmt(d.burnedHours),
          conversationid: match ? match[1] : '',
        };
      });
    setDetailRows(details);
    setIsShowDetails(true);
  }

  // ─── Render ───────────────────────────────────────────────────────────────

  const hasSummary = totalWorkingCapacity !== '';

  return (
    <div className="p-4 space-y-4">
      {/* Title */}
      <div>
        <h3 style={{ fontSize: '22px', color: '#5697b3', fontWeight: 400 }}>Efficiency Report</h3>
        <p style={{ color: '#555', fontSize: '12px', margin: '0 0 12px 0' }}>
          This view contains information on the hours logged by the user per year and month in their
          cards as well as the capacity, total worked, PTO's, productivity and efficiency
          percentages.
        </p>
      </div>

      {/* Filters */}
      <div className="flex items-center gap-3">
        <select
          value={selectedYear}
          onChange={e => setSelectedYear(Number(e.target.value))}
          className="border border-gray-300 rounded px-2 py-1.5 text-sm focus:outline-none focus:ring-1 focus:ring-corgee-blue"
        >
          {years.map(y => (
            <option key={y} value={y}>
              {y}
            </option>
          ))}
        </select>
        <button
          onClick={getResults}
          disabled={isLoading}
          className="px-4 py-1.5 bg-corgee-blue hover:bg-corgee-blueHover text-white text-sm rounded disabled:opacity-60"
        >
          Apply Filters
        </button>
      </div>

      {/* Summary stats */}
      {!isLoading && hasSummary && (
        <div className="flex gap-8 flex-wrap">
          <StatBlock label="Working Capacity" value={totalWorkingCapacity} />
          <StatBlock label="Logged Hours" value={totalLoggedHours} />
          <StatBlock label="PTO Days" value={String(totalPTODays)} />
          <StatBlock label="Productivity" value={totalProductivity + '%'} />
          <StatBlock label="Efficiency" value={totalEfficiency + '%'} />
        </div>
      )}

      {/* Loading */}
      {isLoading && (
        <div className="flex justify-center py-12">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-corgee-blue" />
        </div>
      )}

      {/* General table */}
      {!isLoading && !isShowDetails && (
        <>
          {rows.length > 0 ? (
            <div
              style={{
                overflowX: 'auto',
                overflowY: 'auto',
                maxHeight: 'calc(100vh - 500px)',
                border: '1px solid #ddd',
              }}
            >
              <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '12px' }}>
                <thead>
                  <tr>
                    {(
                      [
                        { col: 'month', label: 'Month' },
                        { col: 'workingcapacity', label: 'Working Capacity' },
                        { col: 'loggedhours', label: 'Logged Hours' },
                        { col: 'ptodays', label: 'PTO Days' },
                        { col: 'productivity', label: 'Productivity' },
                        { col: 'efficiency', label: 'Efficiency' },
                      ] as { col: SortColumn; label: string }[]
                    ).map(({ col, label }) => (
                      <th
                        key={col}
                        onClick={() => sortTableBy(col)}
                        style={{
                          backgroundColor: '#ededed',
                          color: '#666',
                          padding: '4px 8px',
                          fontWeight: 500,
                          cursor: 'pointer',
                          textAlign: 'left',
                          borderTop: 'none',
                          whiteSpace: 'nowrap',
                          position: 'sticky',
                          top: 0,
                        }}
                      >
                        {label}
                        <SortIcon col={col} sortedBy={sortedBy} dir={sortDir} />
                      </th>
                    ))}
                    <th
                      style={{
                        backgroundColor: '#ededed',
                        color: '#666',
                        padding: '4px 8px',
                        fontWeight: 500,
                        textAlign: 'center',
                        borderTop: 'none',
                        position: 'sticky',
                        top: 0,
                      }}
                    >
                      Details
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {rows.map(row => (
                    <tr
                      key={row.id}
                      className="efficiency-report-row"
                      onMouseEnter={e => (e.currentTarget.style.backgroundColor = '')}
                      onMouseLeave={e => (e.currentTarget.style.backgroundColor = '')}
                    >
                      <td
                        style={{ padding: '4px 8px', borderTop: '1px solid #ddd', color: '#333' }}
                      >
                        {row.month}
                      </td>
                      <td
                        style={{ padding: '4px 8px', borderTop: '1px solid #ddd', color: '#333' }}
                      >
                        {row.workingcapacity}
                      </td>
                      <td
                        style={{ padding: '4px 8px', borderTop: '1px solid #ddd', color: '#333' }}
                      >
                        {row.loggedhours}
                      </td>
                      <td
                        style={{ padding: '4px 8px', borderTop: '1px solid #ddd', color: '#333' }}
                      >
                        {row.ptodays}
                      </td>
                      <td
                        style={{ padding: '4px 8px', borderTop: '1px solid #ddd', color: '#333' }}
                      >
                        {row.productivity}
                      </td>
                      <td
                        style={{ padding: '4px 8px', borderTop: '1px solid #ddd', color: '#333' }}
                      >
                        {row.efficiency}
                      </td>
                      <td
                        style={{
                          padding: '4px 8px',
                          borderTop: '1px solid #ddd',
                          textAlign: 'center',
                        }}
                      >
                        {row.showbutton && (
                          <button
                            onClick={() => showDetails(row.id)}
                            className="text-corgee-blue hover:text-corgee-blueHover"
                            title="Show details"
                          >
                            <Plus className="w-4 h-4" />
                          </button>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          ) : (
            <p className="text-center text-gray-500 text-sm py-8">{resultMessage}</p>
          )}
        </>
      )}

      {/* Details table */}
      {!isLoading && isShowDetails && (
        <>
          <div className="flex justify-end">
            <button
              onClick={() => setIsShowDetails(false)}
              className="text-brightGray hover:text-corgee-blue"
              title="Back to general view"
            >
              <RotateCcw className="w-4 h-4" />
            </button>
          </div>
          <div
            style={{
              overflowX: 'auto',
              overflowY: 'auto',
              maxHeight: 'calc(100vh - 500px)',
              border: '1px solid #ddd',
            }}
          >
            <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '12px' }}>
              <thead>
                <tr>
                  {[
                    'Month',
                    'Card Name',
                    'Logged Hours',
                    'Estimated',
                    'Remaining',
                    'Actual',
                    'BurnedHours',
                  ].map(h => (
                    <th
                      key={h}
                      style={{
                        backgroundColor: '#ededed',
                        color: '#666',
                        padding: '4px 8px',
                        fontWeight: 500,
                        textAlign: 'left',
                        borderTop: 'none',
                        position: 'sticky',
                        top: 0,
                      }}
                    >
                      {h}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {detailRows.map(row => (
                  <tr
                    key={row.id}
                    className="efficiency-report-row"
                    onMouseEnter={e => (e.currentTarget.style.backgroundColor = '')}
                    onMouseLeave={e => (e.currentTarget.style.backgroundColor = '')}
                  >
                    <td style={{ padding: '4px 8px', borderTop: '1px solid #ddd', color: '#333' }}>
                      {row.month}
                    </td>
                    <td
                      style={{
                        padding: '4px 8px',
                        borderTop: '1px solid #ddd',
                        color: '#62accb',
                        cursor: 'pointer',
                        fontWeight: 'bold',
                      }}
                      onClick={() =>
                        row.conversationid && openInfoWindow(Number(row.conversationid))
                      }
                    >
                      {row.taskname}
                    </td>
                    <td style={{ padding: '4px 8px', borderTop: '1px solid #ddd', color: '#333' }}>
                      {row.loggedhours}
                    </td>
                    <td style={{ padding: '4px 8px', borderTop: '1px solid #ddd', color: '#333' }}>
                      {row.estimated}
                    </td>
                    <td style={{ padding: '4px 8px', borderTop: '1px solid #ddd', color: '#333' }}>
                      {row.remaining}
                    </td>
                    <td style={{ padding: '4px 8px', borderTop: '1px solid #ddd', color: '#333' }}>
                      {row.actual}
                    </td>
                    <td style={{ padding: '4px 8px', borderTop: '1px solid #ddd', color: '#333' }}>
                      {row.burnedhours}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </>
      )}
    </div>
  );
};

// ─── StatBlock ────────────────────────────────────────────────────────────────

function StatBlock({ label, value }: { label: string; value: string })
{
  return (
    <div className="text-center min-w-[90px]">
      <div className="text-xs text-brightGray leading-tight">{label}</div>
      <div className="text-2xl font-semibold text-corgee-black mt-0.5">{value}</div>
    </div>
  );
}
